import { Navigate } from "react-router";

import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { StartupLoading } from "@/providers/StartupLoading";
import { routes } from "@/routes";

import { AuthenticationState, useAuthenticationState } from "./AuthenticationStateProvider";

interface Props {
  /**
   * This also keeps all the query parameters in the URL. Useful for registration to get the location code.
   */
  preserveQueryParams?: boolean;
  children: React.ReactNode;
}

export function LoggedInGuard({ preserveQueryParams, children }: Props): React.ReactNode {
  const state = useAuthenticationState();

  function getRedirectUrl(rootUrl: string) {
    const currentUrl = location.pathname + location.search + location.hash;

    const newSearchParams = new URLSearchParams(preserveQueryParams ? location.search : "");
    if (
      !preserveQueryParams &&
      currentUrl &&
      currentUrl !== "/" &&
      currentUrl !== routes.authentication.login() &&
      currentUrl !== routes.authentication.multiFactorAuthentication()
    ) {
      newSearchParams.set("redirect", currentUrl);
    }

    if (newSearchParams.size === 0) {
      return rootUrl;
    }

    return rootUrl + "?" + newSearchParams.toString();
  }

  if (state === AuthenticationState.Failed) {
    return <ConnectionFailurePage />;
  }

  if (state === AuthenticationState.Loading) {
    return <StartupLoading />;
  }

  if (state === AuthenticationState.NotLoggedIn) {
    return <Navigate to={getRedirectUrl(routes.authentication.login())} />;
  }

  if (state === AuthenticationState.LoggedInButEmailNotVerified) {
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.size > 0 ? `?${searchParams.toString()}` : "";

    return <Navigate to={routes.authentication.verifyEmail() + query} />;
  }

  if (state === AuthenticationState.LoggedInButMFANotCompleted) {
    return <Navigate to={getRedirectUrl(routes.authentication.multiFactorAuthentication())} />;
  }

  return children;
}
